import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Car, Wallet, Siren, ArrowRight, Sparkles, ScanLine, Brain, ShieldCheck } from 'lucide-react';
import { useLang } from '../i18n';
import { Badge, Reveal } from '../components/ui';

const VN_IMG = 'https://images.unsplash.com/photo-1613131145282-9476375618e1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400';

export default function Home() {
  const { t } = useLang();
  const moments = [
    { to: '/buy', k: 'moment_buy', d: 'buy_desc', icon: Car, n: '01' },
    { to: '/carry', k: 'moment_carry', d: 'carry_desc', icon: Wallet, n: '02' },
    { to: '/claim', k: 'moment_claim', d: 'claim_desc', icon: Siren, n: '03' },
  ];
  return (
    <div data-testid="home-page">
      {/* hero */}
      <section className="max-w-7xl mx-auto px-5 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <Reveal><Badge><Sparkles className="w-3 h-3" /> Digital → Autonomous</Badge></Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-5 leading-[1.05]">
              {t('hero_title_1')}<br />
              <span className="text-jade">{t('hero_title_2')}</span><br />
              {t('hero_title_3')}
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="text-slate-400 text-lg mt-6 max-w-xl leading-relaxed">{t('hero_sub')}</p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/buy" data-testid="hero-buy-cta" className="btn-jade px-7 py-3.5 text-sm inline-flex items-center gap-2">
                {t('get_started')} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/claim" data-testid="hero-claim-cta" className="btn-ghost px-7 py-3.5 text-sm inline-flex items-center gap-2">
                {t('explore')}
              </Link>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="relative">
            <div className="card overflow-hidden">
              <img src={VN_IMG} alt="Vietnam street traffic" className="w-full h-[340px] object-cover opacity-90" />
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 }}
              className="absolute -bottom-6 -left-4 glass rounded-2xl p-4 w-56 border border-jade/30"
            >
              <div className="kicker mb-1">AI Agent</div>
              <div className="text-sm text-slate-200 leading-snug">{t('positioning')}</div>
            </motion.div>
          </div>
        </Reveal>
      </section>

      {/* three moments */}
      <section className="max-w-7xl mx-auto px-5 pb-20">
        <div className="grid md:grid-cols-3 gap-5">
          {moments.map((m, i) => (
            <Reveal key={m.to} delay={i * 0.08}>
              <Link to={m.to} data-testid={`moment-${m.k}`} className="card card-hover p-7 block h-full">
                <div className="flex items-start justify-between">
                  <div className="w-12 h-12 rounded-xl bg-jade/10 border border-jade/30 flex items-center justify-center">
                    <m.icon className="w-6 h-6 text-jade" />
                  </div>
                  <span className="font-mono text-slate-600 text-sm">{m.n}</span>
                </div>
                <h3 className="font-display text-2xl font-bold mt-5">{t(m.k)}</h3>
                <p className="text-slate-400 text-sm mt-2 leading-relaxed">{t(m.d)}</p>
                <div className="mt-5 text-jade text-sm font-medium flex items-center gap-2">
                  {t('nav_' + m.k.split('_')[1].toLowerCase()) || ''} <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* capability strip */}
      <section className="max-w-7xl mx-auto px-5 pb-24">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: ScanLine, t: 'OCR & Vehicle Discovery' },
            { icon: Brain, t: 'Grounded Policy RAG' },
            { icon: Siren, t: 'CV Damage Assessment' },
            { icon: ShieldCheck, t: 'Auditable Decisions' },
          ].map((c, i) => (
            <Reveal key={c.t} delay={i * 0.06}>
              <div className="card p-5 flex items-center gap-3">
                <c.icon className="w-5 h-5 text-jade shrink-0" />
                <span className="text-sm text-slate-300">{c.t}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
